import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useGetDriversBreakdownMutation } from "../../../store/api/lumi/drivers";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { formatDateToUtc } from "../../../utils/formatDate";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import LabelSelector from "./LabelSelector"; // Use the updated LabelSelector component
import SmallMultiple from "./SmallMultiple"; // Updated SmallMultiple supports fixed domain

export function DriversOverTime(props) {
  // State for aggregated chart data and per-driver time series.
  const [chartData, setChartData] = useState([]);
  const [driverData, setDriverData] = useState({});
  const [loading, setLoading] = useState(true);
  const [getDriversBreakdown] = useGetDriversBreakdownMutation();
  const [getVolumes] = useGetConceptsFiltersMutation();
  const [conceptColors, setConceptColors] = useState({});

  // Common x-axis labels and selection state.
  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);

  const labelFontSize = useSelector((state) => {
    const fontSizes = state.fontManager.fontSizes;
    const normalizedKey = Object.keys(fontSizes).find(
      (key) => key.trim() === "labelFontSize"
    );
    return fontSizes[normalizedKey] || 16;
  });

  const { id, selectedFieldDriverName, filters, seletctedType, selectedDateName, interval, smoothLine } = props;

  // Use the concept filter (as a JSON string) for the drivers API.
  const conceptFilter = useMemo(
    () => JSON.stringify(getConceptFilter(props)),
    [props]
  );

  // Optional: trigger a concepts fetch for driver context.
  const getConcepts = useCallback(async () => {
    const filterObj = getConceptFilter(props);
    const { data: dataConcepts } = await getVolumes({
      id: props.id,
      concept_selector: filterObj,
      filter: filters,
    });
    props.setFilterDocCount(dataConcepts.filter_count);
  }, [filters, props.id]);

  const getDrivers = useCallback(async () => {
    setLoading(true);
    const { data } = await getDriversBreakdown({
      id,
      concept_selector: JSON.parse(conceptFilter),
      score_field: selectedFieldDriverName,
      filter: filters,
      match_type: props.seletctedType,
      breakdowns: [{ name: selectedDateName, interval }],
    });

    // data[0].buckets contains driver breakdown per date.
    const buckets = data[0].buckets;
    const conceptsMap = {};
    const conceptColorsMap = {};

    buckets.forEach(({ label, drivers }) => {
      const formattedDate = formatDateToUtc(label, interval);
      drivers.forEach(({ name, average_score, color }) => {
        if (!conceptsMap[name]) {
          conceptsMap[name] = [];
          if (color) {
            conceptColorsMap[name] = color;
          }
        }
        conceptsMap[name].push({
          date: formattedDate,
          [name]: average_score,
        });
      });
    });

    setConceptColors(conceptColorsMap);

    // Merge aggregated data by date (if needed for other purposes)
    const aggregatedMap = Array.from(
      Object.values(conceptsMap)
        .flat()
        .reduce((acc, curr) => {
          if (acc.has(curr.date)) {
            const existingEntry = acc.get(curr.date);
            const updatedEntry = { ...existingEntry };
            for (const key in curr) {
              if (key !== "date") {
                updatedEntry[key] = (updatedEntry[key] || 0) + curr[key];
              }
            }
            acc.set(curr.date, updatedEntry);
          } else {
            acc.set(curr.date, curr);
          }
          return acc;
        }, new Map()).values()
    );
    setChartData(aggregatedMap);

    // Build per-driver time series data.
    if (aggregatedMap.length > 0) {
      const driverMap = {};
      Object.keys(aggregatedMap[0]).forEach((key) => {
        if (key !== "date") {
          driverMap[key] = aggregatedMap.map((entry) => ({
            date: entry.date,
            [key]: entry[key],
          }));
        }
      });
      setDriverData(driverMap);
    }

    // Extract unique x-axis labels.
    const labelSet = new Set();
    aggregatedMap.forEach((entry) => {
      if (entry.date) labelSet.add(entry.date);
    });
    const labels = Array.from(labelSet);
    // Sort labels (assuming format like "Q1 2023").
    labels.sort((a, b) => {
      const yearA = a.match(/\d{4}/) ? parseInt(a.match(/\d{4}/)[0], 10) : 0;
      const yearB = b.match(/\d{4}/) ? parseInt(b.match(/\d{4}/)[0], 10) : 0;
      if (yearA !== yearB) return yearA - yearB;
      const quarterA = a.match(/Q(\d)/) ? parseInt(a.match(/Q(\d)/)[1], 10) : 0;
      const quarterB = b.match(/Q(\d)/) ? parseInt(b.match(/Q(\d)/)[1], 10) : 0;
      return quarterA - quarterB;
    });
    setAllLabels(labels);
    setSelectedLabels(labels); // Initially select all labels.

    setLoading(false);
  }, [
    filters,
    getDriversBreakdown,
    id,
    interval,
    selectedDateName,
    selectedFieldDriverName,
    props.seletctedType,
    conceptFilter,
  ]);

  useEffect(() => {
    setLoading(true);
    const debouncedGetDrivers = debounce(getDrivers, 1000);
    debouncedGetDrivers();
    return () => {
      debouncedGetDrivers.cancel();
    };
  }, [getDrivers]);

  useEffect(() => {
    const debouncedGetConcepts = debounce(getConcepts, 1000);
    debouncedGetConcepts();
    return () => {
      debouncedGetConcepts.cancel();
    };
  }, [getConcepts]);

  // Toggle a label.
  const handleToggleLabel = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  // Filter driverData based on selected labels.
  const filteredDriverData = {};
  Object.entries(driverData).forEach(([driverName, dataPoints]) => {
    filteredDriverData[driverName] = dataPoints.filter((dp) =>
      selectedLabels.includes(dp.date)
    );
  });

  // Compute a global maximum and minimum value across all driver charts.
  let globalMax = 0;
  let globalMin = Infinity;
  Object.entries(filteredDriverData).forEach(([driverName, dataPoints]) => {
    dataPoints.forEach((dp) => {
      const value = dp[driverName];
      if (value !== null && value !== undefined) {
        if (value > globalMax) globalMax = value;
        if (value < globalMin) globalMin = value;
      }
    });
  });
  // Fallback for globalMin if no value found.
  if (globalMin === Infinity) globalMin = 0;

  return (
    <div
      className="drivers-over-time"
      style={{
        height: props.widget.layout.h * 39 - 150,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      {/* Grid of small multiples: one chart per driver */}
      <div style={{ flex: "1 1 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", padding: "0.5rem" }}>
          {Object.keys(filteredDriverData).map((driverName, idx) => {
            const data = filteredDriverData[driverName];
            const colorPalette = [
              "#A5D8FF",
              "#FFC9A8",
              "#FDFFA1",
              "#D2FF99",
              "#E6CCFF",
              "#FFB0C9",
              "#99FFEE",
              "#D5FF99",
              "#FFF59B",
              "#C4C4C4",
            ];
            const color = conceptColors[driverName] || colorPalette[idx % colorPalette.length];
            const metricKey = driverName; // Each chart uses its driver name as key.
            return (
              <div
                key={driverName}
                style={{
                  flex: "0 0 356px",
                  height: "220px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <SmallMultiple
                  data={data}
                  metric={metricKey}
                  color={color}
                  title={driverName}
                  smoothLine={smoothLine}
                  minYAxis={globalMin}
                  maxYAxis={globalMax}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* Common clickable LabelSelector */}
      <div style={{ flex: "0 0 auto", padding: "0.5rem 1rem" }}>
        {allLabels.length > 0 && (
          <LabelSelector
            labels={allLabels}
            selectedLabels={selectedLabels}
            onToggleLabel={handleToggleLabel}
          />
        )}
      </div>
    </div>
  );
}

export default DriversOverTime;
